import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {TicketData} from "types/Ticket";
import TicketForm from "pages/Ticket/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "../../../context/OperatorContext";

function MutateTicket() {
    const {termFor} = useOperator();
    const {id} = useParams()
    const {data: ticket, isLoading} = useQuery(
        ['tickets', {id}],
        () => ngage.tickets.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: TicketData) => ngage.tickets.store(data))
    const update = useMutation((data: TicketData) => ngage.tickets.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: TicketData, {setSubmitting}: FormikHelpers<TicketData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('tickets.all')

        setSubmitting(false)
        navigate('/tickets')
    }

    // Handle store mutation
    async function handleStore(values: TicketData) {
        try {
            await store.mutateAsync(values)
            notify("success", `${termFor('Ticket')} Created`, `A new ${termFor('ticket')} has been created`)
        } catch (error) {
            notify("error", `${termFor('Ticket')} Not Created`, `A new ${termFor('ticket')} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: TicketData) {
        try {
            await update.mutateAsync(values)
            notify("success", `${termFor('Ticket')} Updated`, `The chosen ${termFor('ticket')} has been updated`)
            await invalidate(['tickets', {id}])
        } catch (error) {
            notify("error", `${termFor('Ticket')} Not Updated`, `The chosen ${termFor('ticket')} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Commercial': '/', [termFor('Tickets')]: '/tickets', [`New ${termFor('Ticket')}`]: '/tickets/new'}
    const editBreadcrumbs = {'Commercial': '/', [termFor('Tickets')]: '/tickets', [`Edit ${termFor('Ticket')}`]: `/tickets/${id}/edit`}

    return (
        <Page title={id ? `Edit ${termFor('Ticket')}` : `New ${termFor('Ticket')}`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <TicketForm ticket={ticket} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateTicket;